import React, { useEffect, useState, memo, useMemo } from "react";
import {
  PDFViewer,
  Document,
  Page,
  View,
  Text,
  Image,
  BlobProvider,
} from "@react-pdf/renderer";
import axios from "axios";
import config from "../../config";
import { pdf } from "@react-pdf/renderer";
import Swal from "sweetalert2";

/* =========================
   CONFIG
========================= */
const ROWS_PER_PAGE = 25;

/* =========================
   HOISTED CONSTANTS (NO DESIGN CHANGE)
========================= */
const STUDENT_HEADERS = [
  "Sl No.",
  "Student ID",
  "Candidate Name",
  "Challan No",
  "Father's Name",
  "Mother's Name",
  "DOB",
  "Caste",
  "Category",
  "Gender",
  "Medium",
  "Appearing In",
  "Language 1",
  "Language 2",
  "Compulsory 1",
  "Compulsory 2",
  "Compulsory 3",
  "Additional Subject",
];

const CATEGORY_KEYS = ["EX_REGULAR", "REGULAR", "PRIVATE"];
const loaderStyle = {
  height: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "18px",
  fontWeight: "bold",
};

/* =========================
   STYLE OBJECTS (REUSED)
========================= */
const pageStyle = { padding: 15, fontSize: 10, fontFamily: "Helvetica" };
const rowFlex = { flexDirection: "row" };
const headerRow = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: 10,
};
const tableContainer = {
  marginTop: 10,
  fontSize: 5,
  borderLeft: "1px solid black",
  borderRight: "1px solid black",
};
const tableHeaderRow = {
  flexDirection: "row",
  backgroundColor: "#f0f0f0",
  borderTop: "1px solid black",
  borderBottom: "1px solid black",
};
const cellHeader = {
  flex: 1,
  fontWeight: "bold",
  textAlign: "center",
  fontSize: 6,
  padding: 2,
  borderRight: "1px solid black",
};
const cell = {
  flex: 1,
  textAlign: "center",
  fontSize: 5,
  padding: 2,
  borderRight: "1px solid black",
};

/* =========================
   UTILITY (UNCHANGED)
========================= */
const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

/* =========================
   PDF REPORT (MEMOIZED)
========================= */
const PdfReport = memo(({ groupedData, schooldata }) => (
  <Document>
    {Object.entries(groupedData).map(
      ([challanNo, { full_data, summary_data }]) => {
        const studentChunks = chunkArray(full_data, ROWS_PER_PAGE);

        // 🔥 computed ONCE per challan (no hooks, no ESLint issue)
        const filteredGenderRows = summary_data.gender_summary.filter((row) =>
          Object.entries(row).some(
            ([key, value]) => key !== "GENDER" && value > 0
          )
        );

        const availableColumns = CATEGORY_KEYS.filter((key) =>
          summary_data.gender_summary.some((row) => row[key] > 0)
        );

        const tableHeaders = ["GENDER", ...availableColumns, "TOTAL"];

        return studentChunks.map((students, pageIndex) => {
          const isLastPage = pageIndex === studentChunks.length - 1;

          return (
            <Page
              key={`${challanNo}-${pageIndex}`}
              size="A4"
              orientation="landscape"
              style={pageStyle}
            >
              {/* ================= HEADER ================= */}
              <View style={headerRow}>
                <Image
                  src="../assets/images/jac.png"
                  style={{ width: 35, height: 35, marginRight: 10 }}
                />
                <View style={{ flex: 1, textAlign: "center" }}>
                  <Text style={{ fontSize: 12, fontWeight: "bold" }}>
                    JHARKHAND ACADEMIC COUNCIL, RANCHI
                  </Text>
                  <Text
                    style={{
                      marginTop: 4,
                      fontSize: 10,
                      fontWeight: "bold",
                    }}
                  >
                    List Of Registered Students For Class IX - Examination 2026
                    (Annual)
                  </Text>
                </View>
              </View>

              {/* ================= SCHOOL INFO ================= */}
              <View
                style={{
                  flexDirection: "row",
                  paddingBottom: 2,
                  marginBottom: 2,
                }}
              >
                <View
                  style={{
                    width: "20%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <view style={{ width: "40%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      School Code
                    </Text>
                  </view>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "52%",
                    }}
                  >
                    <Text style={{ fontSize: 10, textAlign: "left" }}>
                      {schooldata.SCHOOL_CODE}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    width: "40%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "20%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      School Name
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "75%",
                    }}
                  >
                    <Text style={{ fontSize: 10, textAlign: "left" }}>
                      {schooldata.SCHOOL_NAME}
                    </Text>
                  </View>
                </View>

                <View
                  style={{
                    width: "20%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "25%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      District
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "70%",
                    }}
                  >
                    <Text style={{ fontSize: 10, textAlign: "left" }}>
                      {schooldata.DISTRICT}
                    </Text>
                  </View>
                </View>
                <View
                  style={{
                    width: "20%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "30%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      Category
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "65%",
                    }}
                  >
                    <Text style={{ fontSize: 10, textAlign: "left" }}>
                      {schooldata.CATEGORY || " "}
                    </Text>
                  </View>
                </View>
              </View>

              <View
                style={{
                  flexDirection: "row",
                  paddingBottom: 2,
                  marginBottom: 2,
                }}
              >
                {/* Sub-Division */}
                <View
                  style={{
                    width: "20%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "40%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      Sub-Division
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "55%",
                    }}
                  >
                    <Text style={{ fontSize: 10 }}>
                      {schooldata.DIVISION_NAME}
                    </Text>
                  </View>
                </View>

                {/* Block */}
                <View
                  style={{
                    width: "40%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "10%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      Block
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "85%",
                    }}
                  >
                    <Text style={{ fontSize: 10 }}>
                      {schooldata.BLOCK_NAME}
                    </Text>
                  </View>
                </View>

                {/* Post Office */}
                <View
                  style={{
                    width: "20%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "35%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      Post Office
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "60%",
                    }}
                  >
                    <Text style={{ fontSize: 10 }}>
                      {schooldata.POST_OFFICE}
                    </Text>
                  </View>
                </View>

                {/* PIN */}
                <View
                  style={{
                    width: "20%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "20%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      PIN
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "75%",
                    }}
                  >
                    <Text style={{ fontSize: 10 }}>
                      {schooldata.PIN_CODE || " "}
                    </Text>
                  </View>
                </View>
              </View>
              <View
                style={{
                  flexDirection: "row",
                  paddingBottom: 2,
                  marginBottom: 2,
                }}
              >
                {/* H.M.'s Mobile No. */}
                <View
                  style={{
                    width: "20%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "55%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      H.M.'s Mobile No.
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "40%",
                    }}
                  >
                    <Text style={{ fontSize: 10 }}>
                      {schooldata.HEAD_MOBILE_NO}
                    </Text>
                  </View>
                </View>

                {/* Email Id. */}
                <View
                  style={{
                    width: "35%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "15%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      Email Id.
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "80%",
                    }}
                  >
                    <Text style={{ fontSize: 10 }}>
                      {schooldata.EMAIL_ADDRESS}
                    </Text>
                  </View>
                </View>

                {/* Telephone No. */}
                <View
                  style={{
                    width: "20%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "45%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      Telephone No.
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "50%",
                    }}
                  >
                    <Text style={{ fontSize: 10 }}>
                      {schooldata.TELEPHONE_NO || ""}
                    </Text>
                  </View>
                </View>
                <View
                  style={{
                    width: "25%",
                    flexDirection: "row",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  <View style={{ width: "58%" }}>
                    <Text style={{ fontSize: 10, fontWeight: "bold" }}>
                      Asstt. H.M.'s Mobile No :
                    </Text>
                  </View>
                  <View
                    style={{
                      borderBottomWidth: 1,
                      borderColor: "black",
                      width: "37%",
                    }}
                  >
                    <Text style={{ fontSize: 10 }}>
                      {schooldata.ASSTISTENCE_TEACHER_CONTACT || " "}
                    </Text>
                  </View>
                </View>
              </View>

              {/* ================= STUDENT TABLE ================= */}
              <View style={tableContainer}>
                <View style={tableHeaderRow}>
                  {STUDENT_HEADERS.map((header, i) => (
                    <Text key={i} style={cellHeader}>
                      {header}
                    </Text>
                  ))}
                </View>

                {students.map((record, rowIndex) => (
                  <View
                    key={rowIndex}
                    wrap={false}
                    style={{ ...rowFlex, borderBottom: "1px solid black" }}
                  >
                    {[
                      pageIndex * ROWS_PER_PAGE + rowIndex + 1,
                      record.CANDIDATE_ID,
                      record.CANDIDATE_NAME,
                      record.CHALLAN_NO,
                      record.FATHER_NAME,
                      record.MOTHER_NAME,
                      record.DOB,
                      record.CASTE_NAME,
                      record.PRINT_CATEGORY?.[0],
                      record.GENDER?.[0],
                      record.MEDIUM_NAME,
                      "ALL",
                      record.SUBJECT_1,
                      record.SUBJECT_2,
                      record.SUBJECT_3,
                      record.SUBJECT_4,
                      record.SUBJECT_5,
                      record.SUBJECT_6,
                    ].map((item, i) => (
                      <Text key={i} style={cell}>
                        {item || "-"}
                      </Text>
                    ))}
                  </View>
                ))}
              </View>

              {/* ================= SUMMARY ================= */}
              {isLastPage && (
                <>
                  <View
                    style={{
                      flexDirection: "row",
                      justifyContent: "space-between",
                      marginTop: 50,
                      fontSize: 8,
                    }}
                  >
                    <View>
                      <Text>
                        i) Category : R = Regular, P = Private, E- Ex-Regular
                      </Text>
                      <Text>
                        ii) Gender : M = Male, F = Female, T = Transgender
                      </Text>
                      <Text>iii) Appearing in : ALL = All Subjects</Text>
                    </View>
                    <View>
                      <Text>Paid by Challan No: {challanNo}</Text>
                      <Text>
                        No of Candidate in the Challan: {summary_data.TOTAL}
                      </Text>
                    </View>
                    <View style={{ marginRight: "50px" }}>
                      <Text>
                        Signature of Head of the Institution with Seal
                      </Text>
                    </View>
                  </View>

                  {/* ================= GENDER SUMMARY TABLE (UNCHANGED) ================= */}
                  <View
                    style={{
                      marginTop: 10,
                      marginHorizontal: 220,
                      fontSize: 7,
                    }}
                    wrap={false}
                  >
                    <View
                      style={{
                        flexDirection: "row",
                        backgroundColor: "#f0f0f0",
                      }}
                    >
                      {tableHeaders.map((header, index) => (
                        <Text
                          key={index}
                          style={{
                            flex: 1,
                            fontWeight: "bold",
                            textAlign: "center",
                            fontSize: 7,
                            borderWidth: 1,
                            borderColor: "black",
                            padding: 2,
                          }}
                        >
                          {header === "GENDER" ? "Gender" : header}
                        </Text>
                      ))}
                    </View>

                    {filteredGenderRows.map((row, rowIndex) => (
                      <View key={rowIndex} style={{ flexDirection: "row" }}>
                        <Text
                          style={{
                            flex: 1,
                            textAlign: "center",
                            fontSize: 7,
                            borderWidth: 1,
                            borderColor: "black",
                            padding: 2,
                          }}
                        >
                          {row.GENDER}
                        </Text>

                        {availableColumns.map((col, colIndex) => (
                          <Text
                            key={colIndex}
                            style={{
                              flex: 1,
                              textAlign: "center",
                              fontSize: 7,
                              borderWidth: 1,
                              borderColor: "black",
                              padding: 2,
                            }}
                          >
                            {row[col] || "-"}
                          </Text>
                        ))}

                        <Text
                          style={{
                            flex: 1,
                            textAlign: "center",
                            fontSize: 7,
                            borderWidth: 1,
                            borderColor: "black",
                            padding: 2,
                          }}
                        >
                          {(row.EX_REGULAR || 0) +
                            (row.REGULAR || 0) +
                            (row.PRIVATE || 0)}
                        </Text>
                      </View>
                    ))}
                  </View>
                </>
              )}
            </Page>
          );
        });
      }
    )}
  </Document>
));

/* =========================
   MAIN COMPONENT
========================= */
const ListofExamineesPDF = () => {
  const [groupedData, setGroupedData] = useState(null);
  const [schooldata, setSchooldata] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null); // 🔥 NEW: store PDF blob URL
  const [generatingPdf, setGeneratingPdf] = useState(false); // 🔥 NEW: track PDF generation

  useEffect(() => {
    fetchData();
  }, []);
  // 🔥 AUTO-GENERATE PDF ON PAGE LOAD (ONCE DATA IS READY)
  useEffect(() => {
    if (groupedData && schooldata && !pdfUrl && !generatingPdf) {
      generatePdf();
    }
  }, [groupedData, schooldata]);

  const fetchData = async () => {
    const token = sessionStorage.getItem("tokenclassxi");
    if (!token) return;

    try {
      setLoading(true);

      const resp = await axios.get(
        `${config.API_BASE_URL}/ListOfExamineesCL11`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setGroupedData(resp.data.data.examinees_data);
      setSchooldata(resp.data.data.school_data);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  /* ===========================
     GENERATE PDF USING BLOB
  =========================== */
  const generatePdf = async () => {
    if (!groupedData || !schooldata) return;

    try {
      setGeneratingPdf(true);
      Swal.fire({
        title: "Preparing PDF",
        text: "Please wait while the PDF is generated...",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const blob = await pdf(
        <PdfReport groupedData={groupedData} schooldata={schooldata} />
      ).toBlob();

      Swal.close();

      // 🔥 Create blob URL and store in state
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);

      setGeneratingPdf(false);
    } catch (error) {
      Swal.close();
      setGeneratingPdf(false);
      console.error(error);
      Swal.fire("Error", "Failed to generate PDF", "error");
    }
  };

  // 🔥 SHOW PDF IN IFRAME INSTEAD OF NEW TAB
  const closePdf = () => {
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl); // cleanup
    }
    setPdfUrl(null);
  };

  const downloadPdf = () => {
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = "ListOfExaminees.pdf";
    a.click();
  };

  if (loading) {
    return <div style={loaderStyle}>Loading data...</div>;
  }

  if (!groupedData || !schooldata) {
    return <div style={loaderStyle}>No data available</div>;
  }

  // 🔥 DISPLAY PDF IN IFRAME
  if (pdfUrl) {
    return (
      <div
        style={{
          height: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* PDF iframe */}
        <iframe
          src={pdfUrl}
          title="PDF Viewer"
          style={{
            flex: 1,
            border: "none",
            width: "100%",
            height: "100%",
          }}
        />
      </div>
    );
  }

  // 🔥 INITIAL BUTTON SCREEN
  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <h3>List Of Registered Students PDF</h3>

      {/* <button
        onClick={generatePdf}
        disabled={generatingPdf}
        style={{
          padding: "12px 24px",
          fontSize: "16px",
          cursor: generatingPdf ? "not-allowed" : "pointer",
          backgroundColor: generatingPdf ? "#ccc" : "#2196F3",
          color: "white",
          border: "none",
          borderRadius: "4px",
          fontWeight: "bold",
        }}
      >
        {generatingPdf ? "Generating..." : "Generate PDF"}
      </button> */}
    </div>
  );
};

export default ListofExamineesPDF;
